/**
 * PostHog analytics utility for Heyward.
 * Import `track` wherever you need to fire an event.
 *
 * Usage:
 *   import { track } from '../../utils/analytics';
 *   track.strategyLockedIn(2034);
 */

import posthog from 'posthog-js';

export const track = {
  // ── Strategy funnel ──────────────────────────────────────────────────────
  strategyStarted:   (mode) =>
    posthog.capture('strategy_started', { mode }),                // 'quick' | 'detailed'

  strategyGoalSet:   (type, amount) =>
    posthog.capture('strategy_goal_set', { goal_type: type, goal_amount: amount }),

  strategyRevealed:  (freedomYear, confidence) =>
    posthog.capture('strategy_revealed', { freedom_year: freedomYear, confidence }),

  strategyLockedIn:  (freedomYear) =>
    posthog.capture('strategy_locked_in', { freedom_year: freedomYear }),

  strategyAbandoned: (atStep) =>
    posthog.capture('strategy_abandoned', { at_step: atStep }),

  // ── Deal pipeline ─────────────────────────────────────────────────────────
  dealCreated:       (source) =>
    posthog.capture('deal_created', { source }),                  // 'manual' | 'email'

  dealPhaseAdvanced: (fromPhase, toPhase) =>
    posthog.capture('deal_phase_advanced', { from: fromPhase, to: toPhase }),

  offerSubmitted:    () =>
    posthog.capture('offer_submitted'),

  documentUploaded:  (docType) =>
    posthog.capture('document_uploaded', { doc_type: docType }),

  // ── Property research ─────────────────────────────────────────────────────
  propertySearched:  (suburb, state, address) =>
    posthog.capture('property_searched', { suburb, state, address }),

  dueDiligenceRun:   (verdict, props = {}) =>
    posthog.capture('due_diligence_run', { verdict, ...props }),  // verdict: 'BUY'|'WALK'|'INVESTIGATE'; props: address, risk_flag_count, confidence, unresolved_check_ids, unresolved_check_count, research_running

  suburbSearched:    (suburb, state) =>
    posthog.capture('suburb_searched', { suburb, state }),

  // A memory write that did not land. surface: the page that asked
  // ('suburb_search'); reason: 'signed_out' | 'error' | 'no_response' |
  // 'not_saved' (the server answered success:false). Until 2026-10-27 the
  // Suburb Search page swallowed these, so a search that never reached the
  // nightly watch list left no trace anywhere.
  memoryWriteFailed: (surface, props = {}) =>
    posthog.capture('memory_write_failed', { surface, ...props }),

  // ── Portfolio ─────────────────────────────────────────────────────────────
  propertyAdded:     () =>
    posthog.capture('property_added'),

  propertyEdited:    () =>
    posthog.capture('property_edited'),

  propertyDeleted:   () =>
    posthog.capture('property_deleted'),

  syncFailed:        (status, transient) =>                       // cloud-save failure — surfaces otherwise-invisible sync errors
    posthog.capture('sync_failed', { status, transient }),

  syncRecovered:     (failedStatus, attempt) =>                   // the failure episode healed — without this, sync_failed is indistinguishable from data loss
    posthog.capture('sync_recovered', { failed_status: failedStatus, attempt }),

  // ── Heyward-as-docs (product guide) ──────────────────────────────────────
  // 'guide' = answered from the product-guide corpus; 'fallback' = honest
  // not-documented reply. Weekly read of this event = the UX-debt list
  // ("if the user has to ask, we failed" — instrumented).
  productQuestionAsked: (answered) =>
    posthog.capture('product_question_asked', { answered }),

  // ── AI Advisor ────────────────────────────────────────────────────────────
  advisorQueried:    () =>
    posthog.capture('advisor_queried'),

  advisorResponded:  (latencyMs) =>
    posthog.capture('advisor_responded', { latency_ms: latencyMs }),

  // ── AI output feedback (👍/👎 on any agent output) ──────────────────────────
  // surface:   'property_verdict' | 'freedom_year' | 'ai_advisor' | 'nudge' | …
  // sentiment: 'up' | 'down'.  Extra context (entity_id, verdict, reason) in props.
  // Layer B: a plain, queryable event. Upgrades later to a trace-linked
  // $ai_feedback survey once LLM-observability trace ids are wired server-side.
  aiFeedback:        (surface, sentiment, props = {}) =>
    posthog.capture('ai_output_feedback', { surface, sentiment, ...props }),

  // ── Auth / activation ─────────────────────────────────────────────────────
  firstDealCreated:  () =>
    posthog.capture('first_deal_created'),

  firstStrategyRun:  () =>
    posthog.capture('first_strategy_run'),

  // ── Acquisition (marketing landing) ──────────────────────────────────────
  // section: which landing block hosted the CTA ('nav'|'hero'|'access'|…).
  // sendBeacon: these CTAs trigger a full-page navigation to /sign-up, so
  // the event must survive the unload. Event name kept as
  // waitlist_cta_clicked for funnel continuity across the self-serve cutover.
  waitlistCtaClicked: (section) =>
    posthog.capture('waitlist_cta_clicked', { section }, { transport: 'sendBeacon' }),

  walkthroughPlayed: (walkthrough) =>
    posthog.capture('walkthrough_played', { walkthrough }),

  // /pricing page view (ADR-025; per-plan CTA intent rides waitlistCtaClicked
  // section values `pricing_plan_*` / `landing_plan_*`).
  pricingViewed: () =>
    posthog.capture('pricing_viewed'),

  // ── Paywall funnel (ADR-025 §2.2 upgrade moment → Stripe) ────────────────
  // quota_modal_shown → quota_modal_cta_clicked → plan_usage_viewed →
  // upgrade_clicked → checkout_started → checkout_returned. Until 2026-08-19
  // nothing between pricing_viewed and the Stripe webhook was observable, so
  // "nobody converts" and "nobody ever saw the wall" were indistinguishable.
  // Every property is server-truthful (taken from the 429 detail / usage
  // payload) — never inferred client-side.
  quotaModalShown: ({ resource, plan, used, limit, upgradeTo }) =>
    posthog.capture('quota_modal_shown', {
      resource,
      plan,
      used,
      limit,
      upgradeable: Boolean(upgradeTo),
      upgrade_to: upgradeTo || null,
    }),

  // cta: 'upgrade' (primary, upgradeable variant) | 'view_usage' (primary,
  // wait/slot variants) | 'dismiss' (the Dismiss button) | 'close' (X or
  // backdrop).
  quotaModalCtaClicked: (cta, { resource, plan, upgradeTo }) =>
    posthog.capture('quota_modal_cta_clicked', {
      cta,
      resource,
      plan,
      upgrade_to: upgradeTo || null,
    }),

  // Settings → Billing & usage tab mounted (the only in-app upgrade surface).
  // Route-level pageviews can't see it: the tab is a ?tab= query change on
  // /settings, not a pathname change.
  planUsageViewed: (plan) =>
    posthog.capture('plan_usage_viewed', { plan: plan || null }),

  // A plan ticket was clicked. sendBeacon: the click leads to a full-page
  // redirect to Stripe within a second, so the event must survive unload.
  upgradeClicked: (plan, interval, source) =>
    posthog.capture('upgrade_clicked', { plan, interval, source }, { transport: 'sendBeacon' }),

  // The checkout session was created and the browser is being handed to
  // Stripe — fired immediately before window.location.assign(url).
  checkoutStarted: (plan, interval) =>
    posthog.capture('checkout_started', { plan, interval }, { transport: 'sendBeacon' }),

  // The checkout-session request failed (status = HTTP status or 'network').
  // Nothing was charged; the member saw the retry copy.
  checkoutFailed: (plan, interval, status) =>
    posthog.capture('checkout_failed', { plan, interval, status: String(status) }),

  // Back from Stripe: outcome 'success' | 'cancelled' (the ?billing= param —
  // plan state itself is never read from it, see billingState.js).
  checkoutReturned: (outcome) =>
    posthog.capture('checkout_returned', { outcome }),

  // ── Upgrade nudges (non-wall surfaces, e.g. the sidebar usage meter) ──────
  // `shown` fires once per mount when the nudge becomes visible — NOT on
  // every render and not while the meter is quiet — so the PostHog ratio
  // shown→clicked is a real conversion rate. reason: 'soft' (≥ soft
  // threshold) | 'cap' (a resource is used up) | 'not_included' (the plan
  // has 0 of a resource the member is asking for, e.g. watched suburbs on Start).
  upgradeNudgeShown: (surface, { plan, resource, used, limit, reason, upgradeTo }) =>
    posthog.capture('upgrade_nudge_shown', {
      surface, plan, resource, used, limit, reason, upgrade_to: upgradeTo || null,
    }),
  upgradeNudgeClicked: (surface, { plan, resource, used, limit, reason, upgradeTo }) =>
    posthog.capture('upgrade_nudge_clicked', {
      surface, plan, resource, used, limit, reason, upgrade_to: upgradeTo || null,
    }),

  // ── First-visit onboarding (FirstVisitSlider) ─────────────────────────────
  // Card-level funnel so drop-off is measurable per slide.
  onboardingCardViewed: (index, name) =>
    posthog.capture('onboarding_card_viewed', { index, name }),

  onboardingGoalSelected: (goal) =>
    posthog.capture('onboarding_goal_selected', { goal }),       // 'freedom'|'buy'|'track'

  onboardingSkipped: (atIndex) =>
    posthog.capture('onboarding_skipped', { at_index: atIndex }),

  onboardingCompleted: (props = {}) =>
    posthog.capture('onboarding_completed', props),              // goal, pain_count, budget_confirmed, duration_ms, fast_completion, skipped_at

  // ── Activation checklist (cold briefing room) ────────────────────────────
  checklistItemClicked: (item, complete) =>
    posthog.capture('checklist_item_clicked', { item, complete }),

  // ── Chunk-load recovery (lazyRetry.js + index.jsx vite:preloadError) ─────
  // Makes silent auto-recovery visible: how often chunk fetches blip
  // (retry succeeded) vs. force a reload (stale deploy / real outage).
  chunkRetrySucceeded: (chunk, attempt) =>
    posthog.capture('chunk_retry_succeeded', { chunk, attempt }),

  // sendBeacon: fired immediately before window.location.reload(), so the
  // event must survive the unload.
  chunkReloadTriggered: (source) =>
    posthog.capture('chunk_reload_triggered', { source }, { transport: 'sendBeacon' }),
};

// ── Error tracking ──────────────────────────────────────────────────────────
// Dedup identical exceptions within a short window so an error loop can't flood
// PostHog's Error Tracking (cost + noise). The manual handlers (window
// error/unhandledrejection + the React error boundaries) don't get PostHog's
// built-in exception throttling, so we add a light client-side one here.
const _recentErrorAt = new Map();
const ERROR_DEDUP_MS = 10000;

export function captureError(error, props = {}) {
  try {
    // Benign aborts (a fetch cancelled on unmount/refresh — e.g. the
    // useAgentLiveRefresh poller) are not real errors; drop them so they don't
    // pollute Error Tracking.
    if (error?.name === 'AbortError') return;
    const key = `${error?.name || 'Error'}:${(error?.message || String(error)).slice(0, 160)}`;
    const now = Date.now();
    if (now - (_recentErrorAt.get(key) || 0) < ERROR_DEDUP_MS) return;
    if (_recentErrorAt.size > 100) _recentErrorAt.clear();
    _recentErrorAt.set(key, now);
    posthog.captureException(error, props);
  } catch {
    /* error tracking must never throw */
  }
}
